import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Typography,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormHelperText,
  Slider,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Cloud as CloudIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';

/**
 * TechnicalPreferencesStep Component - User Story 2
 * Third step in the onboarding wizard for collecting technical preferences
 * 
 * Following VegaDigital Constitution: WCAG 2.1 AA compliance, comprehensive validation
 */

interface TechnicalPreferencesData {
  tech_stack?: string[];
  hosting_preference?: string;
  deployment_preference?: string;
  cms_requirement?: string;
  database_preference?: string;
  performance_requirements?: {
    page_load_time?: number;
    concurrent_users?: number;
    uptime_requirement?: number;
  };
  security_requirements?: string[];
  maintenance_preference?: string;
  backup_requirements?: string;
  monitoring_preference?: string;
  integration_complexity?: string;
  custom_requirements?: string;
}

interface TechnicalPreferencesStepProps {
  data: TechnicalPreferencesData;
  onChange: (data: TechnicalPreferencesData) => void;
  onValidationChange: (isValid: boolean) => void;
  errors?: Record<string, string[]>;
}

const TECH_STACKS = [
  {
    category: 'Frontend',
    options: [
      { value: 'react', label: 'React', description: 'Modern JavaScript library' },
      { value: 'vue', label: 'Vue.js', description: 'Progressive framework' },
      { value: 'angular', label: 'Angular', description: 'Full-featured framework' },
      { value: 'vanilla-js', label: 'Vanilla JavaScript', description: 'Pure JavaScript' }
    ]
  },
  {
    category: 'Backend',
    options: [
      { value: 'laravel', label: 'Laravel (PHP)', description: 'Elegant PHP framework' },
      { value: 'nodejs', label: 'Node.js', description: 'JavaScript runtime' },
      { value: 'python-django', label: 'Python Django', description: 'High-level Python framework' },
      { value: 'ruby-rails', label: 'Ruby on Rails', description: 'Convention over configuration' },
      { value: 'dotnet', label: '.NET Core', description: 'Microsoft framework' }
    ]
  },
  {
    category: 'Database',
    options: [
      { value: 'mysql', label: 'MySQL', description: 'Popular relational database' },
      { value: 'postgresql', label: 'PostgreSQL', description: 'Advanced relational database' },
      { value: 'mongodb', label: 'MongoDB', description: 'Document database' },
      { value: 'sqlite', label: 'SQLite', description: 'Lightweight database' }
    ]
  }
];

const HOSTING_OPTIONS = [
  { value: 'cloud-managed', label: 'Cloud Managed (AWS, Azure, GCP)', description: 'Fully managed cloud services' },
  { value: 'shared-hosting', label: 'Shared Hosting', description: 'Cost-effective shared resources' },
  { value: 'vps', label: 'VPS/Dedicated Server', description: 'Dedicated virtual/physical server' },
  { value: 'on-premise', label: 'On-Premise', description: 'Your own infrastructure' },
  { value: 'no-preference', label: 'No Preference', description: 'Let us recommend the best option' }
];

const CMS_OPTIONS = [
  { value: 'custom', label: 'Custom CMS', description: 'Built specifically for your needs' },
  { value: 'wordpress', label: 'WordPress', description: 'Popular content management system' },
  { value: 'drupal', label: 'Drupal', description: 'Enterprise-grade CMS' },
  { value: 'strapi', label: 'Strapi', description: 'Headless CMS' },
  { value: 'none', label: 'No CMS Required', description: 'Static content or no content management' }
];

const SECURITY_REQUIREMENTS = [
  { value: 'ssl-certificate', label: 'SSL Certificate' },
  { value: 'two-factor-auth', label: 'Two-Factor Authentication' },
  { value: 'data-encryption', label: 'Data Encryption at Rest' },
  { value: 'gdpr-compliance', label: 'GDPR Compliance' },
  { value: 'hipaa-compliance', label: 'HIPAA Compliance' },
  { value: 'pci-compliance', label: 'PCI DSS Compliance' },
  { value: 'regular-security-audits', label: 'Regular Security Audits' },
  { value: 'penetration-testing', label: 'Penetration Testing' },
  { value: 'user-permissions', label: 'Advanced User Permissions' },
  { value: 'audit-logging', label: 'Audit Logging' }
];

const MAINTENANCE_OPTIONS = [
  { value: 'full-managed', label: 'Fully Managed', description: 'We handle all maintenance and updates' },
  { value: 'partial-managed', label: 'Partially Managed', description: 'We handle critical updates, you handle content' },
  { value: 'self-managed', label: 'Self-Managed', description: 'You handle all maintenance with our documentation' },
  { value: 'consultation', label: 'Consultation Only', description: 'We provide guidance as needed' }
];

export const TechnicalPreferencesStep: React.FC<TechnicalPreferencesStepProps> = ({
  data,
  onChange,
  onValidationChange,
  errors = {}
}) => {
  const [localData, setLocalData] = useState<TechnicalPreferencesData>(data);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  // Combined validation and parent update effect
  useEffect(() => {
    const newErrors: Record<string, string> = {};

    // Required field validation
    if (!localData.hosting_preference) {
      newErrors.hosting_preference = 'Please select a hosting preference';
    }

    if (!localData.maintenance_preference) {
      newErrors.maintenance_preference = 'Please select a maintenance preference';
    }

    // Only update errors if they changed
    const errorsChanged = JSON.stringify(newErrors) !== JSON.stringify(localErrors);
    if (errorsChanged) {
      setLocalErrors(newErrors);
    }
    
    const isValid = Object.keys(newErrors).length === 0 && 
                   !!localData.hosting_preference &&
                   !!localData.maintenance_preference;
    
    // Update parent with data and validation
    onChange(localData);
    onValidationChange(isValid);
  }, [localData, localErrors, onChange, onValidationChange]);

  const handleTechStackChange = (value: string, checked: boolean) => {
    setLocalData(prev => ({
      ...prev,
      tech_stack: checked 
        ? [...(prev.tech_stack || []), value]
        : (prev.tech_stack || []).filter(tech => tech !== value)
    }));
  };

  const handleSecurityChange = (value: string, checked: boolean) => {
    setLocalData(prev => ({
      ...prev,
      security_requirements: checked 
        ? [...(prev.security_requirements || []), value]
        : (prev.security_requirements || []).filter(req => req !== value)
    }));
  };

  const handlePerformanceChange = (field: string, value: number) => {
    setLocalData(prev => ({
      ...prev,
      performance_requirements: {
        ...prev.performance_requirements,
        [field]: value
      }
    }));
  };

  const getError = (field: string): string => {
    return errors[field]?.[0] || localErrors[field] || '';
  };

  const hasError = (field: string): boolean => {
    return !!(errors[field]?.length || localErrors[field]);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Technical Preferences
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Help us understand your technical requirements and preferences to recommend the best technology stack.
      </Typography>

      {/* Technology Stack Selection */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon color="primary" />
            <Typography variant="subtitle1">Technology Stack Preferences</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select your preferred technologies (optional - we can recommend the best stack for your needs):
          </Typography>
          {TECH_STACKS.map((category) => (
            <Box key={category.category} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                {category.category}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {category.options.map((tech) => (
                  <Card
                    key={tech.value}
                    sx={{
                      cursor: 'pointer',
                      minWidth: 200,
                      border: localData.tech_stack?.includes(tech.value) ? 2 : 1,
                      borderColor: localData.tech_stack?.includes(tech.value) ? 'primary.main' : 'divider',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                    onClick={() => handleTechStackChange(tech.value, !localData.tech_stack?.includes(tech.value))}
                    data-testid={`tech-${tech.value}`}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {tech.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tech.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* Hosting Preferences */}
      <Box sx={{ my: 3 }}>
        <FormControl component="fieldset" fullWidth error={hasError('hosting_preference')}>
          <FormLabel component="legend">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CloudIcon color="primary" />
              <Typography variant="subtitle1">Hosting Preference *</Typography>
            </Box>
          </FormLabel>
          <RadioGroup
            value={localData.hosting_preference || ''}
            onChange={(e) => setLocalData(prev => ({ ...prev, hosting_preference: e.target.value }))}
          >
            {HOSTING_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                }
                data-testid={`hosting-${option.value}`}
              />
            ))}
          </RadioGroup>
          {hasError('hosting_preference') && (
            <FormHelperText>{getError('hosting_preference')}</FormHelperText>
          )}
        </FormControl>
      </Box>

      {/* CMS Requirements */}
      <Box sx={{ my: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Content Management System</InputLabel>
          <Select
            value={localData.cms_requirement || ''}
            onChange={(e) => setLocalData(prev => ({ ...prev, cms_requirement: e.target.value }))}
            label="Content Management System"
            data-testid="cms-select"
          >
            {CMS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Performance Requirements */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SpeedIcon color="primary" />
            <Typography variant="subtitle1">Performance Requirements</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ spacing: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                Target Page Load Time: {localData.performance_requirements?.page_load_time || 3} seconds
              </Typography>
              <Slider
                value={localData.performance_requirements?.page_load_time || 3}
                onChange={(_, value) => handlePerformanceChange('page_load_time', value as number)}
                min={1}
                max={10}
                step={0.5}
                marks={[
                  { value: 1, label: '1s' },
                  { value: 3, label: '3s' },
                  { value: 5, label: '5s' },
                  { value: 10, label: '10s' }
                ]}
                valueLabelDisplay="auto"
                data-testid="page-load-slider"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                Expected Concurrent Users: {localData.performance_requirements?.concurrent_users || 100}
              </Typography>
              <Slider
                value={localData.performance_requirements?.concurrent_users || 100}
                onChange={(_, value) => handlePerformanceChange('concurrent_users', value as number)}
                min={10}
                max={10000}
                step={10}
                scale={(x) => x}
                marks={[
                  { value: 10, label: '10' },
                  { value: 100, label: '100' },
                  { value: 1000, label: '1K' },
                  { value: 10000, label: '10K' }
                ]}
                valueLabelDisplay="auto"
                data-testid="concurrent-users-slider"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                Required Uptime: {localData.performance_requirements?.uptime_requirement || 99.5}%
              </Typography>
              <Slider
                value={localData.performance_requirements?.uptime_requirement || 99.5}
                onChange={(_, value) => handlePerformanceChange('uptime_requirement', value as number)}
                min={95}
                max={99.99}
                step={0.1}
                marks={[
                  { value: 95, label: '95%' },
                  { value: 99, label: '99%' },
                  { value: 99.5, label: '99.5%' },
                  { value: 99.9, label: '99.9%' },
                  { value: 99.99, label: '99.99%' }
                ]}
                valueLabelDisplay="auto"
                data-testid="uptime-slider"
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Security Requirements */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            <Typography variant="subtitle1">Security & Compliance Requirements</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {SECURITY_REQUIREMENTS.map((requirement) => (
                <Box key={requirement.value} sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 4px)' } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localData.security_requirements?.includes(requirement.value) || false}
                        onChange={(e) => handleSecurityChange(requirement.value, e.target.checked)}
                        data-testid={`security-${requirement.value}`}
                      />
                    }
                    label={requirement.label}
                  />
                </Box>
              ))}
            </Box>
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {/* Maintenance Preference */}
      <Box sx={{ my: 3 }}>
        <FormControl component="fieldset" fullWidth error={hasError('maintenance_preference')}>
          <FormLabel component="legend">
            <Typography variant="subtitle1">Maintenance & Support Preference *</Typography>
          </FormLabel>
          <RadioGroup
            value={localData.maintenance_preference || ''}
            onChange={(e) => setLocalData(prev => ({ ...prev, maintenance_preference: e.target.value }))}
            sx={{ mt: 1 }}
          >
            {MAINTENANCE_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                }
                data-testid={`maintenance-${option.value}`}
              />
            ))}
          </RadioGroup>
          {hasError('maintenance_preference') && (
            <FormHelperText>{getError('maintenance_preference')}</FormHelperText>
          )}
        </FormControl>
      </Box>

      {/* Custom Requirements */}
      <Box sx={{ mt: 3 }}>
        <TextField
          fullWidth
          label="Additional Technical Requirements"
          value={localData.custom_requirements || ''}
          onChange={(e) => setLocalData(prev => ({ ...prev, custom_requirements: e.target.value }))}
          helperText="Any specific technical requirements, integrations, or preferences not covered above"
          multiline
          rows={3}
          inputProps={{
            'aria-label': 'Additional Technical Requirements',
            'data-testid': 'custom-requirements-input'
          }}
        />
      </Box>

      {/* Selected Technologies Summary */}
      {localData.tech_stack?.length ? (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Selected Technologies:</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {localData.tech_stack.map((tech) => {
              // Find the label for the tech
              const techOption = TECH_STACKS.flatMap(category => category.options)
                .find(option => option.value === tech);
              return (
                <Chip
                  key={tech}
                  label={techOption?.label || tech}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              );
            })}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};